import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const COMPANY_LOGO = "/assets/img/Website_logo_color logo-black text-1.png";
const PLAY_STORE_URL =
  "https://play.google.com/store/search?q=Instatenders&c=apps&hl=en_IN";
const PAGE_LIMIT = 25;

const PO_STATUS_COLORS = {
  Draft: "#6B7280",
  "Internal issue": "#B45309",
  Acknowledged: "#2563EB",
  "SPO Created": "#7C3AED",
  "Dispatch in progress": "#0891B2",
  Dispatched: "#0F766E",
  Delivered: "#059669",
  "Replacement in progress": "#D97706",
  "GRN Completed": "#0EA5E9",
  "Payment completed": "#BE185D",
  "Order on Hold": "#CA8A04",
  "Order Closed": "#374151",
};

const PO_STATUS_ALIASES = {
  Acknwoledged: "Acknowledged",
  "Dispached in progress": "Dispatch in progress",
  "Dispatched in progress": "Dispatch in progress",
  "Replacemnt in progress": "Replacement in progress",
  "Order on hold": "Order on Hold",
  "Order closed": "Order Closed",
};

const TRACKING_STEPS = [
  { key: "acknowledged", label: "Acknowledged", icon: "fa-file-circle-check" },
  { key: "dispatch", label: "Dispatched", icon: "fa-truck-fast" },
  { key: "delivered", label: "Delivered", icon: "fa-box-open" },
  { key: "grn", label: "GRN", icon: "fa-clipboard-check" },
  { key: "payment", label: "Payment", icon: "fa-indian-rupee-sign" },
];

const INTRO_FEATURES = [
  { label: "Line-item details", icon: "fa-boxes-stacked" },
  { label: "Dispatch updates", icon: "fa-truck" },
  { label: "GRN & payment status", icon: "fa-clipboard-check" },
];

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function normalizeStatus(status) {
  const value = String(status || "").trim();
  return PO_STATUS_ALIASES[value] || value;
}

function getStatusColor(status) {
  return PO_STATUS_COLORS[normalizeStatus(status)] || "#2563EB";
}

function displayValue(value) {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  return value;
}

function formatDate(value) {
  if (!value || value === "-") return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function getTrackingStepIndex(tracker) {
  const status = normalizeStatus(tracker?.poStatus);
  const grn = String(tracker?.grnStatus || "").toLowerCase();
  const payment = String(tracker?.paymentStatus || "").toLowerCase();

  if (
    status === "Payment completed" ||
    payment.includes("complete") ||
    payment.includes("paid")
  ) {
    return 5;
  }
  if (status === "GRN Completed" || grn.includes("complete")) return 4;
  if (status === "Delivered") return 3;
  if (status === "Dispatched" || status === "Dispatch in progress") return 2;
  if (
    ["Acknowledged", "SPO Created", "Order on Hold", "Internal issue"].includes(
      status,
    )
  ) {
    return 1;
  }
  return 0;
}

function getStepState(stepIndex, currentStep) {
  if (currentStep > stepIndex) return "done";
  if (currentStep === stepIndex) return "active";
  return "pending";
}

const TrackerCardSkeleton = () => (
  <div className="track-card track-card-skeleton" aria-hidden="true">
    <div className="track-skel track-skel-head" />
    <div className="track-skel track-skel-line" />
    <div className="track-skel track-skel-line short" />
    <div className="track-skel track-skel-steps" />
    <div className="track-skel track-skel-grid" />
  </div>
);

const TrackingPO = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPo = searchParams.get("poNo") || "";

  const [poInput, setPoInput] = useState(initialPo);
  const [activePoNo, setActivePoNo] = useState(initialPo.trim());
  const [trackers, setTrackers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(Boolean(initialPo.trim()));
  const [loadError, setLoadError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedCardIds, setExpandedCardIds] = useState({});

  const fetchTrackers = useCallback(async () => {
    if (!activePoNo) {
      setTrackers([]);
      setTotalPages(1);
      setTotalRecords(0);
      return;
    }

    setIsLoading(true);
    setLoadError("");

    try {
      const response = await axios.post(API_URL, {
        action: "get-trackers",
        page: currentPage,
        limit: PAGE_LIMIT,
        poNo: activePoNo,
      });

      const responseData = parseApiResponse(response.data);
      const payload = responseData?.data || {};
      const list = Array.isArray(payload.trackers) ? payload.trackers : [];

      if (responseData?.success === false) {
        setTrackers([]);
        setTotalPages(1);
        setTotalRecords(0);
        setLoadError(
          responseData.message || "Unable to fetch tracking details.",
        );
        return;
      }

      setTrackers(list);
      const totalCount =
        Number(payload.totalCount) ||
        Number(payload.pagination?.totalCount) ||
        list.length;
      const apiTotalPages =
        Number(payload.totalPages) ||
        Number(payload.pagination?.totalPages) ||
        0;
      const computedTotalPages = Math.max(
        1,
        Math.ceil(totalCount / PAGE_LIMIT) || 1,
      );
      setTotalPages(Math.max(apiTotalPages, computedTotalPages));
      setTotalRecords(totalCount);
    } catch (error) {
      console.error("Fetch trackers error:", error);
      setTrackers([]);
      setTotalPages(1);
      setTotalRecords(0);
      setLoadError(
        "Unable to load tracking details right now. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [activePoNo, currentPage]);

  const resetTrackingSearch = useCallback(() => {
    setActivePoNo("");
    setHasSearched(false);
    setTrackers([]);
    setCurrentPage(1);
    setTotalPages(1);
    setTotalRecords(0);
    setLoadError("");
    setIsLoading(false);
    setSearchParams({});
  }, [setSearchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activePoNo) {
      fetchTrackers();
    }
  }, [activePoNo, fetchTrackers]);

  const handlePoInputChange = (event) => {
    const value = event.target.value;
    setPoInput(value);
    if (!value.trim()) {
      resetTrackingSearch();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = poInput.trim();
    if (!trimmed) {
      setPoInput("");
      resetTrackingSearch();
      return;
    }

    setLoadError("");
    setHasSearched(true);
    setCurrentPage(1);
    setExpandedCardIds({});
    setActivePoNo(trimmed);
    setSearchParams({ poNo: trimmed });
  };

  const handlePageChange = (page) => {
    const next = Math.min(Math.max(1, page), totalPages);
    if (next !== currentPage) {
      setCurrentPage(next);
      setExpandedCardIds({});
      window.scrollTo({ top: 320, behavior: "smooth" });
    }
  };

  const getTrackerCardId = (tracker, index) =>
    tracker._id || `${tracker.poNo}-${index}`;

  const toggleCardDetails = (cardId) => {
    setExpandedCardIds((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const startItem =
    trackers.length > 0 ? (currentPage - 1) * PAGE_LIMIT + 1 : 0;
  const endItem = Math.min(currentPage * PAGE_LIMIT, totalRecords);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i += 1) {
        pages.push(i);
      }
      return pages;
    }

    if (currentPage <= 3) {
      for (let i = 1; i <= 4; i += 1) {
        pages.push(i);
      }
      pages.push("ellipsis", totalPages);
      return pages;
    }

    if (currentPage >= totalPages - 2) {
      pages.push(1, "ellipsis");
      for (let i = totalPages - 3; i <= totalPages; i += 1) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1, "ellipsis", currentPage - 1, currentPage, currentPage + 1);
    pages.push("ellipsis", totalPages);
    return pages;
  };

  const currentYear = new Date().getFullYear();

  const searchForm = (
    <form className="track-search-card" onSubmit={handleSubmit}>
      <label htmlFor="po-number-input" className="track-search-label">
        Reference PO Number <span aria-hidden="true">*</span>
      </label>
      <div className="track-search-combo">
        <div className="track-search-input-wrap">
          <i className="fa-solid fa-hashtag" aria-hidden="true" />
          <input
            id="po-number-input"
            type="text"
            value={poInput}
            onChange={handlePoInputChange}
            placeholder="Po Number"
            autoComplete="off"
            required
          />
        </div>
        <button
          type="submit"
          className="track-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" />
              Searching...
            </>
          ) : (
            <>
              <i className="fa-solid fa-magnifying-glass" />
              Track Order
            </>
          )}
        </button>
      </div>
      {loadError && !isLoading && (
        <p className="track-search-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          {loadError}
        </p>
      )}
    </form>
  );

  const heroSection = (
    <section className="track-hero track-hero-compact">
      <div className="track-hero-inner">
        <span className="auth-eyebrow">
          <i className="fa-solid fa-truck-ramp-box" /> Delivery Status Report
        </span>
        <h1>
          Track your <span>Purchase Order</span>
        </h1>
        {searchForm}
      </div>
    </section>
  );

  const introPanel = (
    <div className="track-intro-panel">
      <div className="track-intro-head">
        <div className="track-intro-icon">
          <i className="fa-solid fa-route" />
        </div>
        <div>
          <h2>Real-time order visibility</h2>
          <p>
            Search by PO number to see material details, dispatch progress,
            and fulfilment milestones in one place.
          </p>
        </div>
      </div>

      <div className="track-intro-features">
        {INTRO_FEATURES.map((feature) => (
          <span key={feature.label} className="track-intro-feature">
            <i className={`fa-solid ${feature.icon}`} />
            {feature.label}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div
      className={`auth-shell accent-custom track-shell${
        hasSearched ? " track-has-results" : ""
      }`}
    >
      <header className="auth-header">
        <div className="auth-header-inner">
          <Link to="/" className="auth-brand" aria-label="InstaTenders Home">
            <img src={COMPANY_LOGO} alt="InstaTenders" />
          </Link>

          <nav className="auth-nav">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="auth-store-btn"
            >
              <i className="fa-brands fa-google-play" aria-hidden="true" />
              Android
            </a>
            <Link to="/signin" className="auth-store-btn track-signin-btn">
              <i className="fa-solid fa-right-to-bracket" aria-hidden="true" />
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main className="track-main">
        {heroSection}

        {!hasSearched ? (
          <section className="track-results-section track-intro-section">
            <div className="track-results-inner">{introPanel}</div>
          </section>
        ) : (
          <section className="track-results-section">
              <div className="track-results-inner">
                {isLoading ? (
              <div className="track-results-stack">
                <div className="track-results-meta">Loading order details...</div>
                {Array.from({ length: 2 }).map((_, index) => (
                  <TrackerCardSkeleton key={index} />
                ))}
              </div>
                ) : loadError ? (
                  <div className="track-empty">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <p>{loadError}</p>
                    <button
                      type="button"
                      className="auth-submit-btn track-retry-btn"
                      onClick={fetchTrackers}
                    >
                      Try again
                    </button>
                  </div>
                ) : trackers.length === 0 ? (
                  <div className="track-empty">
                    <i className="fa-solid fa-box-open" />
                    <p>
                      No tracking records found for{" "}
                      <strong>&ldquo;{activePoNo}&rdquo;</strong>.
                    </p>
                    <p className="track-empty-hint">
                      Please verify the PO number and try again.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="track-results-meta">
                      Showing{" "}
                      <strong>
                        {startItem.toLocaleString("en-IN")}–
                        {endItem.toLocaleString("en-IN")} of{" "}
                        {totalRecords.toLocaleString("en-IN")}
                      </strong>{" "}
                      line item{totalRecords === 1 ? "" : "s"} for PO{" "}
                      <strong>{activePoNo}</strong>
                      {totalPages > 1 && (
                        <>
                          {" "}
                          (Page {currentPage} of {totalPages})
                        </>
                      )}
                    </div>

                    <div className="track-results-stack">
                  {trackers.map((tracker, index) => {
                    const status = normalizeStatus(tracker.poStatus);
                    const statusColor = getStatusColor(status);
                    const currentStep = getTrackingStepIndex(tracker);
                    const serialNo =
                      (currentPage - 1) * PAGE_LIMIT + index + 1;
                    const cardId = getTrackerCardId(tracker, index);
                    const isExpanded = Boolean(expandedCardIds[cardId]);

                    return (
                      <article
                        key={cardId}
                        className="track-card"
                        style={{ "--track-accent": statusColor }}
                      >
                        <header className="track-card-header">
                          <div className="track-card-title-block">
                            <span className="track-serial">#{serialNo}</span>
                            <div>
                              <h2>{displayValue(tracker.poNo)}</h2>
                              <p>
                                <i className="fa-regular fa-building" />
                                {displayValue(tracker.customerName)}
                              </p>
                            </div>
                          </div>
                          <span
                            className="track-status-badge"
                            style={{
                              backgroundColor: `${statusColor}18`,
                              color: statusColor,
                              borderColor: `${statusColor}40`,
                            }}
                          >
                            <i className="fa-solid fa-circle-dot" />
                            {displayValue(status)}
                          </span>
                        </header>

                        <div className="track-material-block">
                          <div className="track-material-code">
                            <small>Material Code</small>
                            <strong>{displayValue(tracker.materialCode)}</strong>
                          </div>
                          <p className="track-material-desc">
                            {displayValue(tracker.itemDesc)}
                          </p>
                        </div>

                        <div className="track-timeline" aria-label="Order progress">
                          {TRACKING_STEPS.map((step, stepIndex) => {
                            const stepNumber = stepIndex + 1;
                            const state = getStepState(stepNumber, currentStep);
                            return (
                              <div
                                key={step.key}
                                className={`track-step track-step-${state}`}
                              >
                                <div className="track-step-marker">
                                  <i className={`fa-solid ${step.icon}`} />
                                </div>
                                <span className="track-step-label">
                                  {step.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          className={`track-detail-toggle${isExpanded ? " is-expanded" : ""}`}
                          onClick={() => toggleCardDetails(cardId)}
                          aria-expanded={isExpanded}
                          aria-controls={`track-details-${cardId}`}
                        >
                          <span>Order details</span>
                          <i
                            className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"}`}
                            aria-hidden="true"
                          />
                        </button>

                        {isExpanded && (
                          <div
                            id={`track-details-${cardId}`}
                            className="track-detail-grid"
                          >
                            <div className="track-detail-item">
                              <small>PO Date</small>
                              <span>{formatDate(tracker.poDate)}</span>
                            </div>
                            <div className="track-detail-item">
                              <small>Qty Ordered</small>
                              <span>{displayValue(tracker.quantityOrdered)}</span>
                            </div>
                            <div className="track-detail-item">
                              <small>Qty Dispatched</small>
                              <span>
                                {displayValue(tracker.quantityDispatched)}
                              </span>
                            </div>
                            <div className="track-detail-item">
                              <small>Dispatch Date</small>
                              <span>{formatDate(tracker.dispatchDate)}</span>
                            </div>
                            <div className="track-detail-item">
                              <small>Delivery Date</small>
                              <span>{formatDate(tracker.deliveryDate)}</span>
                            </div>
                            <div className="track-detail-item">
                              <small>GRN Status</small>
                              <span>{displayValue(tracker.grnStatus)}</span>
                            </div>
                            <div className="track-detail-item">
                              <small>Payment Status</small>
                              <span>{displayValue(tracker.paymentStatus)}</span>
                            </div>
                            <div className="track-detail-item">
                              <small>Project Status</small>
                              <span>{displayValue(tracker.projectStatus)}</span>
                            </div>
                          </div>
                        )}

                        {isExpanded && displayValue(tracker.comments) !== "-" && (
                          <div className="track-comments">
                            <small>Comments</small>
                            <p>{tracker.comments}</p>
                          </div>
                        )}

                        <footer className="track-card-footer">
                          <span>
                            <i className="fa-regular fa-clock" />
                            Last updated {formatDate(tracker.updatedAt)}
                          </span>
                          {tracker.updatedBy?.name && (
                            <span>
                              <i className="fa-regular fa-user" />
                              {tracker.updatedBy.name}
                            </span>
                          )}
                        </footer>
                      </article>
                    );
                  })}
                </div>

                    {totalPages > 1 && (
                      <>
                        <p className="track-pagination-meta">
                          {startItem}–{endItem} of {totalRecords} line items
                          (25 per page)
                        </p>
                        <nav
                          className="leads-pagination track-pagination"
                          aria-label="Tracking pages"
                        >
                          <button
                            type="button"
                            className="leads-page-btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                          >
                            <i className="fa-solid fa-chevron-left" />
                          </button>
                          {getPageNumbers().map((page, pageIndex) =>
                            page === "ellipsis" ? (
                              <span
                                key={`ellipsis-${pageIndex}`}
                                className="leads-page-ellipsis"
                                aria-hidden="true"
                              >
                                ...
                              </span>
                            ) : (
                              <button
                                key={page}
                                type="button"
                                className={`leads-page-btn${
                                  page === currentPage ? " active" : ""
                                }`}
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            ),
                          )}
                          <button
                            type="button"
                            className="leads-page-btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                          >
                            <i className="fa-solid fa-chevron-right" />
                          </button>
                        </nav>
                      </>
                    )}
                  </>
                )}
              </div>
            </section>
        )}
      </main>

      <footer className="auth-footer leads-footer">
        <div className="auth-footer-bottom">
          <p>{currentYear} © InstaTenders Solutions Pvt Ltd, All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TrackingPO;
